/**
 * Начисления за электроэнергию за месяц — разметка 1:1 с legacy
 * admin_snt_monthes_stchets.tpl (включая мобильную версию модала рассылки).
 *
 * ВАЖНО: в legacy суммы (за период / начислено / потери / к оплате) считал САМ шаблон
 * (строки 411, 465-485). В rework их считает бекенд (Invoices::electroBreakdown,
 * поля day_diff/night_diff/total_diff/day_sum/night_sum/accrued/loss_sum/total) —
 * фронт только ОТОБРАЖАЕТ, ничего не пересчитывает.
 *
 * Сверх legacy — третий канал рассылки квитанций, push в приложение (в legacy приложения
 * не было). Его получатель — не абонент с телефоном/почтой, а УЧАСТОК с зарегистрированным
 * устройством: бекенд помечает такие строки флагом has_push, и только у них видна галочка.
 *
 * Inline-JS шаблона перенесён в обработчики React с сохранением поведения:
 *  - счётчики «Email/Push/Telegram/Max: N Абон.» (#abon_* и *_mobi; SMS-канал отключён);
 *  - пустой выбор → «Выберите абонентов!» в #send_desktop_p / #send_mobi_p, отправки нет;
 *  - кнопка Email у строки — confirm-модал «Подтвердить отправку квитанции?» ([data-confirm]);
 *  - смена месяца в <select> = submit формы (в legacy GET-форма #gen_kvitancia_month).
 * Рассылка защищена одноразовым токеном blocker (GET /api/admin/mailing/token) —
 * как скрытое поле blocker в обеих формах шаблона.
 *
 * Legacy-баг сохранён: send-all ВСЕГДА уходит за предыдущий календарный месяц,
 * выбранный месяц он игнорирует (Invoices::sendKvitanciaAll).
 *
 * Оба окна (рассылка и confirm) — на общем <Modal>: закрытие по фону/Esc, блокировка прокрутки.
 * Вёрстка Bootstrap 3 та же; десктоп/мобайл-формы лежат в теле окна, а их кнопки «Отправить» —
 * в подвале окна и связаны с формами через атрибут form= (form_id / form_id_mobi).
 *
 * API: GET /api/admin/invoices/month?month_tarif=Y-m, POST /api/admin/invoices/fetch,
 *      POST /api/admin/invoices/kvitancia/pdf (PDF-байты, отправляем нативной формой),
 *      POST /api/admin/invoices/kvitancia/send, POST /api/admin/invoices/kvitancia/send-all.
 */

import { Fragment, useCallback, useEffect, useState } from 'react'

import { api, ApiError } from '@/api/client'
import { Modal } from '@/components/Modal'
import { useApi } from '@/lib/useApi'

/** Строка реестра: snt_month_schet + владелец + расчёт бекенда (electroBreakdown). */
interface SchetRow {
  smsid: number
  chetch_number: string | null
  month_number: string
  date_start: string | null
  date_end: string | null
  date_start_en_count: string | null
  date_end_en_count: string | null
  tarif_day_start: string | null
  tarif_day_end: string | null
  tarif_n_start: string | null
  tarif_n_end: string | null
  familia: string | null
  imuy: string | null
  otchestvo: string | null
  position: string | null // название участка (t_area.name)
  rassl_email: string | null
  rassl_sms: string | null
  // посчитано бекендом (в legacy это считал шаблон)
  day_diff: number
  night_diff: number
  total_diff: number
  day_sum: number
  night_sum: number
  accrued: number
  loss_sum: number
  total: number
}

/** Абонент, которому квитанция за прошлый месяц ещё не отправлена (Invoices::monthList → rassl). */
interface RasslRow {
  user_id: number
  area_fk: number
  familia: string | null
  imuy: string | null
  otchestvo: string | null
  email: string | null
  tel: string | null
  rassl_sms: string | null
  rassl_email: string | null
  /** У участка есть приложение — значит, ему можно отправить push (rw_push_tokens). */
  has_push: boolean
  /** У участка привязан Telegram (rw_telegram_links). */
  has_telegram: boolean
  /** У участка привязан Max (rw_max_links). */
  has_max: boolean
}

interface TariffRow {
  id: number
  month: string
  tarif_day: string
  tarif_n: string
  loss: string
}

interface MonthData {
  month_tarif: string
  tarif: TariffRow | null
  stcheest: SchetRow[]
  rassl: RasslRow[]
}

interface FetchResult {
  month_tarif: string
  created: number
  /** Понятное объяснение результата (создано N / нет показаний / все уже созданы). */
  message?: string
}

interface SendAllResult {
  emails_sent: number
  push_sent: number
  telegram_sent?: number
  max_sent?: number
  month: string
}

interface TokenResponse {
  blocker: string
}

/** Названия месяцев как в Smarty date_format ...:'rus' (modifier.date_format.php). */
const MONTHS_RUS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
]

/** admin.css/newapproach.css — так же, как их подключает admin.tpl (в index.html их нет). */
function useAdminCss(): void {
  useEffect(() => {
    for (const href of ['/assets/css/admin.css', '/assets/css/newapproach.css']) {
      if (document.head.querySelector(`link[href="${href}"]`) !== null) continue
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = href
      document.head.appendChild(link)
    }
  }, [])
}

/** date('Y-m', strtotime("first day of this month -$back month")). */
function monthValue(back: number): string {
  const now = new Date()
  const date = new Date(now.getFullYear(), now.getMonth() - back, 1)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

/** {...|date_format:'%m %Y':'':'rus'} → «Январь 2026». */
function monthLabel(value: string): string {
  const [year, month] = value.split('-')
  return `${MONTHS_RUS[Number.parseInt(month, 10) - 1] ?? month} ${year}`
}

/** {$data.tarif.month|date_format:'%d %m %Y':'':'rus'} → «01 Январь 2026». */
function fullDateRus(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return ''
  const [year, month, day] = value.split(' ')[0].split('-')
  if (!year || !month || !day) return value
  return `${day} ${MONTHS_RUS[Number.parseInt(month, 10) - 1] ?? month} ${year}`
}

/** Список месяцев: {for $foo=1 to 12} — 12 предыдущих месяцев. */
const MONTH_OPTIONS: string[] = Array.from({ length: 12 }, (_, index) => monthValue(index + 1))

/** «{if ($item.x == '')}0{/if}{$item.x}» — пустое показание печаталось как 0. */
function orZero(value: string | null): string {
  return value === null || value === '' ? '0' : value
}

/** ФИО одной строкой (familia/imuy/otchestvo из snt_users). */
function fio(row: { familia: string | null; imuy: string | null; otchestvo: string | null }): string {
  return [row.familia, row.imuy, row.otchestvo].filter((part) => part !== null && part !== '').join(' ')
}

/**
 * «Квитанция» — PDF в соседней вкладке.
 *
 * В legacy это была нативная POST-форма с target="_blank". Так больше нельзя: эндпоинт
 * закрыт Bearer-токеном, а браузер, отправляя форму (как и переходя по ссылке), заголовок
 * Authorization не шлёт — сервер отвечал «Требуется авторизация администратора».
 * Поэтому запрос делаем сами, с заголовком, а PDF отдаём как blob (api.downloadFile).
 */
async function openKvitanciaPdf(monthNumber: string, chetchNumber: string): Promise<void> {
  await api.downloadFile(
    '/api/admin/invoices/kvitancia/pdf',
    `Квитанция ${chetchNumber}.pdf`,
    true,
    { month_tarif: monthNumber, chetch_number: chetchNumber },
  )
}

export function AdminInvoicesPage() {
  useAdminCss()

  const [month, setMonth] = useState(MONTH_OPTIONS[0])
  const { data, reload } = useApi<MonthData>(`/api/admin/invoices/month?month_tarif=${month}`)
  const token = useApi<TokenResponse>('/api/admin/mailing/token')

  const [blocker, setBlocker] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [busy, setBusy] = useState(false)
  const [oks, setOks] = useState<string[]>([])
  const [errors, setErrors] = useState<string[]>([])
  const [sendError, setSendError] = useState('') // #send_desktop_p / #send_mobi_p
  const [confirmRow, setConfirmRow] = useState<SchetRow | null>(null)

  // Чекбоксы модала: отмечены по умолчанию у всех, кому есть куда слать.
  // SMS-канал отключён — колонки телефона/SMS здесь больше нет.
  const [emailSel, setEmailSel] = useState<number[]>([])
  // Push/Telegram/Max — каналы сверх legacy, каждый со своим списком: подписчики разные.
  const [pushSel, setPushSel] = useState<number[]>([])
  const [telegramSel, setTelegramSel] = useState<number[]>([])
  const [maxSel, setMaxSel] = useState<number[]>([])

  // Не показываем квитанции, по которым не пришли данные из АИИС (snt.spb.ru): у них пустой
  // date_start — тогда вместо кнопки «Квитанция» была бы надпись «Абонент не найден в базе
  // snt.spb.ru», а сама квитанция всё равно нулевая и бесполезная (решение владельца).
  const rows = (data?.stcheest ?? []).filter((item) => item.date_start !== null && item.date_start !== '')
  const rassl = data?.rassl ?? []
  const tarif = data?.tarif ?? null

  useEffect(() => {
    if (token.data !== null) setBlocker(token.data.blocker)
  }, [token.data])

  // Подпись списка получателей: меняется, только если список действительно другой.
  const rasslKey = (data?.rassl ?? []).map((item) => item.user_id).join(',')

  /**
   * Отметки «выбран» ставим ЗАНОВО, только когда сменился САМ СПИСОК получателей
   * (другой месяц, добавился абонент) — а не на каждое обновление данных.
   *
   * Раньше эффект висел на объекте `data`, и это ломало работу: SWR перезапрашивает
   * данные при возврате на вкладку, приходит НОВЫЙ объект с теми же строками — и выбор,
   * который админ только что расставил руками, затирался «отмечены все».
   */
  useEffect(() => {
    if (data === null) return
    setEmailSel(
      data.rassl
        .filter((item) => item.rassl_email === '1' && item.email !== null && item.email !== '')
        .map((item) => item.user_id),
    )
    setPushSel(data.rassl.filter((item) => item.has_push).map((item) => item.user_id))
    setTelegramSel(data.rassl.filter((item) => item.has_telegram).map((item) => item.user_id))
    setMaxSel(data.rassl.filter((item) => item.has_max).map((item) => item.user_id))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- намеренно только по составу списка
  }, [rasslKey])

  function toggle(list: number[], id: number): number[] {
    return list.includes(id) ? list.filter((item) => item !== id) : [...list, id]
  }

  // Стабильные onClose (useCallback), чтобы Modal не переподписывал Esc/фокус на каждый ререндер
  // окна (чекбоксы в списке рассылки перерисовывают страницу на каждый клик).

  /** Закрытие окна рассылки: гасим сообщение «Выберите абонентов!» (#send_desktop_p / #send_mobi_p). */
  const closeSendModal = useCallback(() => {
    setShowModal(false)
    setSendError('')
  }, [])

  /** Закрытие confirm-модала = кнопка «Отмена» (отправки не происходит). */
  const closeConfirm = useCallback(() => setConfirmRow(null), [])

  /** Свежий одноразовый токен рассылки (legacy $_SESSION['blocker'] + скрытое поле формы). */
  async function ensureBlocker(): Promise<string> {
    if (blocker !== null) return blocker
    const fresh = await api.get<TokenResponse>('/api/admin/mailing/token')
    setBlocker(fresh.blocker)
    return fresh.blocker
  }

  /**
   * «Обновить квитанции» — legacy дёргал крон-URL /admin/snt_monthes_stchets_updater
   * (он отдаёт пустое тело, не JSON-конверт). Здесь — админский эндпоинт с тем же смыслом:
   * создать недостающие счета за выбранный месяц из АИИС.
   */
  async function updateInvoices(): Promise<void> {
    setBusy(true)
    setOks([])
    setErrors([])
    try {
      const result = await api.post<FetchResult>('/api/admin/invoices/fetch', { month_tarif: month })
      // Бекенд шлёт понятный message (создано N / нет показаний / все уже созданы);
      // при его отсутствии — прежний текст.
      setOks([result.message ?? `Квитанции обновлены. Создано счетов: ${result.created}`])
      reload()
    } catch (e: unknown) {
      setErrors([e instanceof ApiError ? e.message : 'Не удалось обновить квитанции'])
    } finally {
      setBusy(false)
    }
  }

  /** Кнопка Email у строки: legacy data-confirm → confirm-модал → POST send_kvitancia_email. */
  async function sendKvitancia(row: SchetRow): Promise<void> {
    setConfirmRow(null)
    setBusy(true)
    setOks([])
    setErrors([])
    try {
      const result = await api.post<{ email: string; month: string }>('/api/admin/invoices/kvitancia/send', {
        month_tarif: row.month_number,
        chetch_number: row.chetch_number ?? '',
      })
      setOks([`Квитанция за ${result.month} отправлена на ${result.email}`])
      reload()
    } catch (e: unknown) {
      setErrors([e instanceof ApiError ? e.message : 'Не удалось отправить квитанцию'])
    } finally {
      setBusy(false)
    }
  }

  /** «Отправить» в модале: submit с emails[]/push[]/telegram[]/max[]/blocker. (SMS отключён.) */
  async function sendAll(): Promise<void> {
    if (emailSel.length + pushSel.length + telegramSel.length + maxSel.length < 1) {
      setSendError('Выберите абонентов!')
      return
    }
    if (
      !window.confirm(
        `Разослать квитанции за предыдущий месяц? Email — ${emailSel.length}, ` +
          `Push — ${pushSel.length}, Telegram — ${telegramSel.length}, Max — ${maxSel.length}.\n` +
          'Письма и уведомления уйдут РЕАЛЬНЫМ жителям СНТ. Отменить отправку будет нельзя.',
      )
    ) {
      return
    }

    setBusy(true)
    setSendError('')
    setOks([])
    setErrors([])
    try {
      const result = await api.post<SendAllResult>('/api/admin/invoices/kvitancia/send-all', {
        blocker: await ensureBlocker(),
        emails: emailSel,
        push: pushSel,
        telegram: telegramSel,
        max: maxSel,
      })
      setBlocker(null) // токен одноразовый — следующая отправка возьмёт новый
      setShowModal(false)
      setOks([
        `Отправлено. Email: ${result.emails_sent}, Push: ${result.push_sent}, ` +
          `Telegram: ${result.telegram_sent ?? 0}, Max: ${result.max_sent ?? 0}`,
      ])
      reload()
    } catch (e: unknown) {
      setBlocker(null)
      setErrors([e instanceof ApiError ? e.message : 'Ошибка рассылки'])
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="row">
        <div className="col-xs-6">
          {oks.map((text, index) => (
            <p key={index} className="okke" style={{ float: 'left' }}>
              {text}
            </p>
          ))}
          {errors.map((text, index) => (
            <span key={index} style={{ color: '#ce4844', float: 'left' }}>
              {text}
            </span>
          ))}
        </div>
      </div>
      <div className="row add-form">
        <button
          type="button"
          style={{ position: 'absolute', top: 20, right: 328, background: '#00870a' }}
          className="hidden-xs bt"
          disabled={busy}
          onClick={() => void updateInvoices()}
        >
          Обновить квитанции
        </button>

        <button
          type="button"
          style={{ position: 'absolute', top: 20, right: 45 }}
          className="hidden-xs bt"
          onClick={() => setShowModal(true)}
        >
          Отправить квитанции за текущий месяц
        </button>
        <button
          type="button"
          style={{ zoom: '80%' }}
          className="hidden-sm hidden-md hidden-lg xs-right bt"
          onClick={() => setShowModal(true)}
        >
          Отправить квитанции за текущий месяц
        </button>

        {/* Modal — цели отправки квитанций */}
        <Modal
          open={showModal}
          onClose={closeSendModal}
          title="Цели отправки квитанций"
          size="lg"
          footer={
            rassl.length > 0 ? (
              <>
                {/* desktop: кнопка/ошибка/blocker формы #form_id (form=... — форма выше, в теле окна) */}
                <div className="hidden-xs">
                  <p id="send_desktop_p">{sendError}</p>
                  <input type="hidden" name="blocker" value={blocker ?? ''} readOnly form="form_id" />
                  <button
                    className="bt"
                    type="submit"
                    form="form_id"
                    name="send_kvitancia_all"
                    id="send_desktop"
                    disabled={busy}
                  >
                    Отправить
                  </button>
                </div>

                {/* mobile */}
                <div className="hidden-sm hidden-md hidden-lg">
                  <p id="send_mobi_p">{sendError}</p>
                  <input type="hidden" name="blocker" value={blocker ?? ''} readOnly form="form_id_mobi" />
                  <button
                    className="bt"
                    type="submit"
                    form="form_id_mobi"
                    name="send_kvitancia_all"
                    id="send_mobi"
                    disabled={busy}
                  >
                    Отправить
                  </button>
                </div>
              </>
            ) : undefined
          }
        >
          {/* desktop */}
          <form
            className="add-form hidden-xs"
            method="POST"
            id="form_id"
            onSubmit={(event) => {
              event.preventDefault()
              void sendAll()
            }}
          >

            {rassl.length === 0 && (
              <p style={{ textAlign: 'center' }}>За текущий месяц, все квитанции были отправленны.</p>
            )}

            {rassl.length > 0 && (
              <>
                <p className="margin_null" style={{ paddingLeft: 5 }}>
                  <b>Email:</b> <span id="abon_emails">{emailSel.length}</span> Абон. <b>Push:</b>{' '}
                  <span id="abon_push">{pushSel.length}</span> Абон. <b>Telegram:</b>{' '}
                  <span id="abon_telegram">{telegramSel.length}</span> Абон. <b>Max:</b>{' '}
                  <span id="abon_max">{maxSel.length}</span> Абон.
                </p>
                <table className="table table-hover table-stripted table-bordered" style={{ marginTop: 15 }}>
                  <thead>
                    <tr>
                      <th rowSpan={2}>№</th>
                      <th rowSpan={2}>ФИО</th>
                      <th rowSpan={2}>Email</th>
                      <th colSpan={4} style={{ whiteSpace: 'nowrap' }}>
                        Отправлять по
                      </th>
                    </tr>
                    <tr>
                      <th className="checkbox_modal_stc">Email</th>
                      <th className="checkbox_modal_stc">Push</th>
                      <th className="checkbox_modal_stc">Telegram</th>
                      <th className="checkbox_modal_stc">Max</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rassl.map((item) => (
                      <tr key={item.user_id}>
                        {/* Номер рисует CSS-счётчик (td.rownums:before), поэтому td пустой —
                            иначе цифра выводилась бы дважды (счётчик + текст). */}
                        <td className="rownums" />
                        <td className="mob_td_fio">
                          {item.familia} {item.imuy} {item.otchestvo}
                        </td>
                        <td className="td_em_tl">
                          {item.email}
                          {(item.email === null || item.email === '') && (
                            <span style={{ color: 'red' }}>Email не указан</span>
                          )}
                        </td>
                        <td className="checkbox_modal_stc">
                          {item.rassl_email === '1' && item.email !== null && item.email !== '' && (
                            <input
                              type="checkbox"
                              name="emails[]"
                              value={item.user_id}
                              checked={emailSel.includes(item.user_id)}
                              onChange={() => setEmailSel((list) => toggle(list, item.user_id))}
                              className="emails_sel"
                            />
                          )}
                        </td>
                        {/* Push/Telegram/Max — только у участков с этим каналом; rassl_* тут ни при чём. */}
                        <td className="checkbox_modal_stc">
                          {item.has_push && (
                            <input
                              type="checkbox"
                              name="push[]"
                              value={item.user_id}
                              checked={pushSel.includes(item.user_id)}
                              onChange={() => setPushSel((list) => toggle(list, item.user_id))}
                              className="push_sel"
                            />
                          )}
                        </td>
                        <td className="checkbox_modal_stc">
                          {item.has_telegram && (
                            <input
                              type="checkbox"
                              name="telegram[]"
                              value={item.user_id}
                              checked={telegramSel.includes(item.user_id)}
                              onChange={() => setTelegramSel((list) => toggle(list, item.user_id))}
                            />
                          )}
                        </td>
                        <td className="checkbox_modal_stc">
                          {item.has_max && (
                            <input
                              type="checkbox"
                              name="max[]"
                              value={item.user_id}
                              checked={maxSel.includes(item.user_id)}
                              onChange={() => setMaxSel((list) => toggle(list, item.user_id))}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </form>

          {/* mobile */}
          <form
            className="add-form hidden-sm hidden-md hidden-lg"
            method="POST"
            id="form_id_mobi"
            onSubmit={(event) => {
              event.preventDefault()
              void sendAll()
            }}
          >

            {rassl.length === 0 && (
              <p style={{ textAlign: 'center' }}>За текущий месяц, все квитанции были отправленны.</p>
            )}

            {rassl.length > 0 && (
              <>
                <p className="margin_null" style={{ paddingLeft: 5 }}>
                  <b>Email:</b> <span id="abon_emails_mobi">{emailSel.length}</span> Абон. <b>Push:</b>{' '}
                  <span id="abon_push_mobi">{pushSel.length}</span> Абон. <b>Telegram:</b>{' '}
                  <span id="abon_telegram_mobi">{telegramSel.length}</span> Абон. <b>Max:</b>{' '}
                  <span id="abon_max_mobi">{maxSel.length}</span> Абон.
                </p>
                {rassl.map((item) => (
                  <table key={item.user_id} className="table table-hover table-stripted" style={{ marginTop: 15 }}>
                    <tbody>
                      <tr className="border_bottom">
                        <td className="wid_mob_td">
                          <b>ФИО:</b>{' '}
                        </td>
                        <td className="mob_td_fio" colSpan={2}>
                          {fio(item)}
                        </td>
                      </tr>
                      {item.rassl_email === '1' && item.email !== null && item.email !== '' && (
                        <tr
                          className="border_bottom RowClassEmail"
                          onClick={() => setEmailSel((list) => toggle(list, item.user_id))}
                        >
                          <td className="wid_mob_td">
                            <b>Email:</b>{' '}
                          </td>
                          <td className="td_em_tl">{item.email}</td>
                          <td className="checkbox_modal_stc">
                            <input
                              type="checkbox"
                              name="emails[]"
                              value={item.user_id}
                              checked={emailSel.includes(item.user_id)}
                              onClick={(event) => event.stopPropagation()}
                              onChange={() => setEmailSel((list) => toggle(list, item.user_id))}
                              className="emails_sel_mobi"
                            />
                          </td>
                        </tr>
                      )}
                      {item.has_push && (
                        <tr
                          className="border_bottom RowClassTel"
                          onClick={() => setPushSel((list) => toggle(list, item.user_id))}
                        >
                          <td className="wid_mob_td">
                            <b>Push:</b>{' '}
                          </td>
                          <td className="td_em_tl">приложение</td>
                          <td className="checkbox_modal_stc">
                            <input
                              type="checkbox"
                              name="push[]"
                              value={item.user_id}
                              checked={pushSel.includes(item.user_id)}
                              onClick={(event) => event.stopPropagation()}
                              onChange={() => setPushSel((list) => toggle(list, item.user_id))}
                              className="push_sel_mobi"
                            />
                          </td>
                        </tr>
                      )}
                      {item.has_telegram && (
                        <tr
                          className="border_bottom RowClassTel"
                          onClick={() => setTelegramSel((list) => toggle(list, item.user_id))}
                        >
                          <td className="wid_mob_td">
                            <b>Telegram:</b>{' '}
                          </td>
                          <td className="td_em_tl">чат</td>
                          <td className="checkbox_modal_stc">
                            <input
                              type="checkbox"
                              name="telegram[]"
                              value={item.user_id}
                              checked={telegramSel.includes(item.user_id)}
                              onClick={(event) => event.stopPropagation()}
                              onChange={() => setTelegramSel((list) => toggle(list, item.user_id))}
                            />
                          </td>
                        </tr>
                      )}
                      {item.has_max && (
                        <tr
                          className="border_bottom RowClassTel"
                          onClick={() => setMaxSel((list) => toggle(list, item.user_id))}
                        >
                          <td className="wid_mob_td">
                            <b>Max:</b>{' '}
                          </td>
                          <td className="td_em_tl">чат</td>
                          <td className="checkbox_modal_stc">
                            <input
                              type="checkbox"
                              name="max[]"
                              value={item.user_id}
                              checked={maxSel.includes(item.user_id)}
                              onClick={(event) => event.stopPropagation()}
                              onChange={() => setMaxSel((list) => toggle(list, item.user_id))}
                            />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                ))}
              </>
            )}
          </form>
        </Modal>

        <div className="col-xs-6 col-md-2 col-md-offset-2 xs-zoom">
          <table className="table">
            <tbody>
              <tr style={{ textAlign: 'center' }}>
                <td>
                  <label>Выберите месяц</label>
                </td>
              </tr>
              <tr>
                <td>
                  <select
                    name="month_tarif"
                    className="form-control"
                    required
                    value={month}
                    onChange={(event) => setMonth(event.target.value)}
                  >
                    {MONTH_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {monthLabel(option)}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="col-xs-6 col-md-6 xs-zoom">
          <table className="table table-hover">
            <thead>
              <tr>
                <th colSpan={4} style={{ textAlign: 'center' }}>
                  Тариф для выбранного месяца
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  Действует с: <i>{fullDateRus(tarif?.month)}</i>
                </td>
                <td>
                  День: <i>{tarif?.tarif_day} руб.</i>
                </td>
                <td>
                  Ночь: <i>{tarif?.tarif_n} руб.</i>
                </td>
                <td>
                  Потери: <i>{tarif?.loss} %</i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="col-xs-12 xs-zoom" style={{ paddingTop: 15 }}>
          <table cellSpacing={1} className="table table-stripted table-hover">
            <thead>
              <tr>
                <td rowSpan={4}>№</td>
                <td rowSpan={4}>
                  Абонент
                  <br />
                  Точка учета
                  <br />№ Счетчика
                </td>
                <td>Дата начала</td>
                <td rowSpan={4}>День</td>
                <td rowSpan={4}>Ночь</td>
                <td rowSpan={4}>Всего</td>
              </tr>
              <tr>
                <td>Дата конечная</td>
              </tr>
              <tr>
                <td>За период</td>
              </tr>
              <tr>
                <td>К оплате</td>
              </tr>
            </thead>
            <tbody>
              {rows.map((item, index) => (
                <Fragment key={item.smsid}>
                  <tr style={{ borderTop: '2px solid #dddddd' }}>
                    <td rowSpan={6}>{index + 1}</td>
                    <td rowSpan={6} style={{ maxWidth: 200 }}>
                      <div className="row">
                        <div className="col-xs-6">
                          {item.familia} {item.imuy} {item.otchestvo}
                          <br />
                          Участок №{item.position}
                          <br />
                          {item.chetch_number}
                          <br />
                        </div>
                        <div className="col-xs-6" style={{ paddingLeft: 0 }}>
                          {/*
                            Каждая кнопка — в своей блочной обёртке, поэтому «Квитанция» и
                            «Email» стоят друг под другом. В legacy эту роль играли два
                            отдельных <form> (каждый со своим POST); формы больше не нужны —
                            отправка идёт из JS, — но без блочной обёртки кнопки стали
                            инлайновыми и разъезжались по одной строке.
                          */}
                          {item.date_start !== null && item.date_start !== '' && (
                            <div>
                              <button
                                type="button"
                                name="gen_kvitancia"
                                className="bt"
                                style={{ paddingTop: 5, paddingBottom: 5 }}
                                onClick={() => {
                                  void openKvitanciaPdf(item.month_number, item.chetch_number ?? '').catch(
                                    (e: unknown) =>
                                      setErrors([e instanceof ApiError ? e.message : 'Не удалось открыть квитанцию']),
                                  )
                                }}
                              >
                                Квитанция
                              </button>
                            </div>
                          )}
                          {(item.date_start === null || item.date_start === '') && (
                            <>
                              Абонент не найден <br /> в базе snt.spb.ru
                            </>
                          )}
                          {item.rassl_email === '1' && (
                            <div>
                              <button
                                type="button"
                                name="send_kvitancia_email"
                                value={item.chetch_number ?? ''}
                                className="bt"
                                style={{
                                  paddingTop: 5,
                                  paddingBottom: 5,
                                  width: 88,
                                  marginTop: 70,
                                }}
                                disabled={busy}
                                onClick={() => setConfirmRow(item)}
                              >
                                <span className="glyphicon glyphicon-share" aria-hidden="true" />
                                &nbsp;Email
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      {(item.date_start === null || item.date_start === '') && 'Нет информации'}
                      {item.date_start}
                    </td>
                    <td>{orZero(item.tarif_day_start)} кВт*ч</td>
                    <td>{orZero(item.tarif_n_start)} кВт*ч</td>
                    <td>{orZero(item.date_start_en_count)} кВт*ч</td>
                  </tr>
                  <tr>
                    <td>
                      {(item.date_end === null || item.date_end === '') && 'Нет информации'}
                      {item.date_end}
                    </td>
                    <td>{orZero(item.tarif_day_end)} кВт*ч</td>
                    <td>{orZero(item.tarif_n_end)} кВт*ч</td>
                    <td>{orZero(item.date_end_en_count)} кВт*ч</td>
                  </tr>
                  <tr>
                    <td>За период</td>
                    <td>{item.day_diff} кВт*ч</td>
                    <td>{item.night_diff} кВт*ч</td>
                    <td>{item.total_diff} кВт*ч</td>
                  </tr>
                  <tr>
                    <td>Начисленно</td>
                    <td>{item.day_sum} руб.</td>
                    <td>{item.night_sum} руб.</td>
                    <td>{item.accrued} руб.</td>
                  </tr>
                  <tr>
                    <td>Потери</td>
                    <td />
                    <td />
                    <td>{item.loss_sum} руб.</td>
                  </tr>
                  <tr>
                    <td>К оплате</td>
                    <td />
                    <td />
                    <td>{item.total} руб.</td>
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && (
            <center>
              <h1>Квитанции отсутствуют</h1>
            </center>
          )}
        </div>
      </div>

      {/* confirm-модал шаблона ([data-confirm] → «Подтвердить отправку квитанции?») */}
      <Modal
        open={confirmRow !== null}
        onClose={closeConfirm}
        size="sm"
        dialogClassName="confirm-modal"
        footer={
          <>
            <a
              href="#"
              className="btn btn-primary yesBtn btn-sm"
              onClick={(event) => {
                event.preventDefault()
                if (confirmRow !== null) void sendKvitancia(confirmRow)
              }}
            >
              Подтвердить
            </a>
            <button type="button" className="btn btn-secondary abortBtn btn-sm" onClick={closeConfirm}>
              Отмена
            </button>
          </>
        }
      >
        Подтвердить отправку квитанции?
      </Modal>
    </>
  )
}
